import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import { useEffect, useRef, useState } from 'react';
import { copilotThinkingDot, copilotThinkingText, copilotThinkingContainer } from '../styles';

const GENERIC_LABELS = [
  'Thinking',
  'Working on it',
  'Pulling data',
  'Crunching numbers',
  'Cogitating',
  'Wrangling rows',
  'Connecting dots',
  'Synthesizing',
  'Digging in',
  'Almost there',
];

const ROTATION_INTERVAL_MS = 3000;

function pickRandomGeneric(previous: string | null): string {
  for (let i = 0; i < 8; i++) {
    const candidate = GENERIC_LABELS[Math.floor(Math.random() * GENERIC_LABELS.length)];
    if (candidate !== previous) return candidate;
  }
  return GENERIC_LABELS[0];
}

type ThinkingIndicatorProps = {
  activityLabel?: string;
};

export function ThinkingIndicator({ activityLabel }: ThinkingIndicatorProps = {}) {
  const initial = activityLabel ?? pickRandomGeneric(null);
  const [label, setLabel] = useState(initial);
  const tickRef = useRef(0);
  const lastGenericRef = useRef<string | null>(null);

  useEffect(() => {
    tickRef.current = 0;
    lastGenericRef.current = null;
    setLabel(activityLabel ?? pickRandomGeneric(null));

    const interval = setInterval(() => {
      tickRef.current += 1;
      // The activity label dominates: show it on every tick except every 6th,
      // so a generic phrase only peeks through ~17% of the time as a brief
      // change-up.
      const useActivity = activityLabel != null && tickRef.current % 6 !== 0;
      if (useActivity) {
        setLabel(activityLabel);
        return;
      }
      const next = pickRandomGeneric(lastGenericRef.current);
      lastGenericRef.current = next;
      setLabel(next);
    }, ROTATION_INTERVAL_MS);

    return () => clearInterval(interval);
  }, [activityLabel]);

  return (
    <Box sx={copilotThinkingContainer}>
      <Box sx={copilotThinkingDot} />
      <Typography sx={copilotThinkingText}>{label}</Typography>
    </Box>
  );
}
